import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { selectCurrentProfileType } from "newui/features/Auth/authSelectors";
import { useAppDispatch, useAppSelector } from "newui/store/hooks";
import {
	createContext,
	useMemo,
	useCallback,
	useState,
	useEffect,
} from "react";
import { useSelector } from "react-redux";
import { isProcurerNBidder } from "utils/helpers";
import { setCurrentProfileType } from "newui/features/Auth/authSlice";
import { useClientAccountPaths, useSupplierAccountPaths } from "newui/utils/navigation";
import { CompanyProfileType } from "newui/features/Auth/types/CompanyProfileType";
import { navigate } from "@reach/router";

export const ProfileContext = createContext("client");

const getUserProfileType = (role) => {
	return {
		currentRole: role === "client" ? "Procurer" : "Bidder",
		isClientProfileActive: role === "client",
	};
};

export const ProfileContextProvider = ({ children }) => {
	const role = useAppSelector(selectCurrentProfileType);
	const clientPaths = useClientAccountPaths();
	const supplierPaths = useSupplierAccountPaths();
	const dispatch = useAppDispatch();

	const { currentUser } = useCurrentUser();
	const company = useCurrentCompany();

	const userId = currentUser?.id;

	const currentProfile = getUserProfileType(role).currentRole;
	const isClientProfileActive = getUserProfileType(role).isClientProfileActive;


	const rfqTotal = useSelector((state) => state?.supplier_rfq?.total);

	const isRfqFeatureEnabled =
		role === "client" ? company?.enable_rfx_functionality : rfqTotal > 0;

	const isAccountSwitchable = isProcurerNBidder(company);

	const changeProfileType = useCallback((profileType) => {

		// safely navigate to 'home' page for respective profile
		navigate(profileType === CompanyProfileType.CLIENT ? clientPaths.home : supplierPaths.home, { replace: true });
		dispatch(setCurrentProfileType(profileType));
	}, [clientPaths, supplierPaths, dispatch]);

	const [forceRestartTour, setForceRestartTour] = useState(false);

	const resetTour = useCallback(async () => {
		setForceRestartTour(true);
	}, []);

	const contextValue = useMemo(
		() => ({
			isClientProfileActive,
			currentProfile,
			isAccountSwitchable,
			changeProfileType,
			isRfqFeatureEnabled,
			userId,
			forceRestartTour,
			setForceRestartTour,
			resetTour,
		}),
		[
			changeProfileType,
			currentProfile,
			isClientProfileActive,
			isAccountSwitchable,
			isRfqFeatureEnabled,
			userId,
			forceRestartTour,
			setForceRestartTour,
			resetTour,
		]
	);

	return (
		<ProfileContext.Provider value={contextValue}>
			{children}
		</ProfileContext.Provider>
	);
};
